/**
 * Граница безопасной отмены (ТЗ §10.1, §14.2).
 *
 * Экран «Мои записи» обязан показать, до какого момента отмена безопасна, и после этого
 * момента сменить подпись кнопки на «Отменить (поздняя отмена)». Расчёт обязан совпасть
 * с серверным `isLate` из `packages/backend/src/services/appointmentService.ts`:
 *
 *     remainingMs < cancelWindowHours * 3_600_000
 *
 * ГРАНИЦА ВКЛЮЧИТЕЛЬНАЯ: ровно на дедлайне отмена ещё безопасная (у сервера строгое `<`).
 * Разойдись мы здесь на одну миллисекунду в другую сторону — клиент нажал бы «отмена без
 * последствий», а получил бы запись, помеченную организации как поздняя.
 *
 * Часовой пояс в этом файле не участвует намеренно: и `startsAt`, и `now` — абсолютные
 * моменты, а вычитание часов от абсолютного момента даёт абсолютный момент в любом поясе.
 * Пояс нужен только на показе метки («до 12:30») — это делает `formatTimeInTz`.
 */

const MS_PER_HOUR = 3_600_000;

export interface CancelWindow {
  /** Момент, до которого (включительно) отмена без последствий. */
  readonly deadline: Date;
  /** Окно уже закрылось: `now` строго позже дедлайна. */
  readonly isLate: boolean;
}

/**
 * Дедлайн безопасной отмены.
 *
 * Некорректное окно — исключение, а не подстановка нуля: окно приходит с сервера, и
 * «0 часов» означало бы «отменяй когда угодно, последствий нет» ровно там, где данные
 * потерялись (AGENTS.md, правило 7).
 */
export function safeCancelDeadline(startsAt: Date, cancelWindowHours: number): Date {
  if (!Number.isFinite(cancelWindowHours) || cancelWindowHours < 0) {
    throw new Error(`Недопустимое окно отмены: ${cancelWindowHours}`);
  }
  const startsAtMs = startsAt.getTime();
  if (!Number.isFinite(startsAtMs)) {
    throw new Error('Недопустимое время начала записи');
  }
  return new Date(startsAtMs - cancelWindowHours * MS_PER_HOUR);
}

/** Отмена поздняя, если `now` прошёл дедлайн. Строго позже — см. границу в шапке файла. */
export function isLateCancel(now: Date, deadline: Date): boolean {
  return now.getTime() > deadline.getTime();
}

export function cancelWindowFor(
  startsAt: Date,
  cancelWindowHours: number,
  now: Date,
): CancelWindow {
  const deadline = safeCancelDeadline(startsAt, cancelWindowHours);
  return { deadline, isLate: isLateCancel(now, deadline) };
}
